/**
 * ============================================================
 * KEJJA OS — src/pages/settings/ProfilePage.tsx
 * ============================================================
 */

import { useState, useEffect, type FormEvent } from 'react';
import { Save, Check } from 'lucide-react';
import { useProfile, useUpdateProfile } from '@/hooks/useSettings';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { EmojiPicker } from '@/components/EmojiPicker';
import { cn } from '@/lib/utils';

import { useTheme as useAppTheme } from '@/hooks/useTheme';

export default function ProfilePage() {
  const { data, isLoading } = useProfile();
  const updateMut = useUpdateProfile();

  const [displayName, setDisplayName] = useState('');
  const [avatarEmoji, setAvatarEmoji] = useState('');
  const [motto, setMotto] = useState('');
  const { setTheme: applyAppTheme } = useAppTheme();
  const [theme, setTheme] = useState<'system' | 'light' | 'dark'>('system');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data?.profile) {
      setDisplayName(data.profile.displayName ?? data.profile.name ?? '');
      setAvatarEmoji(data.profile.avatarEmoji ?? '');
      setMotto(data.profile.motto ?? '');
      setTheme((data.profile.theme as 'system' | 'light' | 'dark') ?? 'system');
    }
  }, [data]);

  if (isLoading) return <Skeleton className="h-64" />;
  if (!data?.profile) return <div className="text-sm text-muted-foreground">Could not load profile.</div>;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setSaved(false);
    updateMut.mutate({
      displayName: displayName.trim() || null,
      avatarEmoji: avatarEmoji || null,
      motto: motto.trim() || null,
      theme,
    }, { onSuccess: () => { setSaved(true); setTimeout(() => setSaved(false), 2000); } });
  };

  const p = data.profile;

  return (
    <div className="space-y-4 pb-12">
      <Card>
        <CardContent className="p-5">
          <form onSubmit={submit} className="space-y-4">
            {/* Avatar + display name */}
            <div className="flex items-start gap-3">
              <div className="mt-1">
                <EmojiPicker value={avatarEmoji} onChange={setAvatarEmoji} />
              </div>
              <div className="flex-1">
                <Label className="text-xs">Display name</Label>
                <Input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="What should Kejja call you?"
                  className="mt-1"
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  Account name: <span className="font-mono">{p.name ?? '—'}</span>
                  {' · '}
                  {p.email}
                </p>
              </div>
            </div>

            <div>
              <Label className="text-xs">Your motto (optional)</Label>
              <Input
                value={motto}
                onChange={(e) => setMotto(e.target.value)}
                placeholder="A personal tagline visible to your household"
                className="mt-1"
              />
            </div>

            <div>
              <Label className="text-xs">Appearance</Label>
              <div className="flex gap-1.5 mt-1">
                {(['system', 'light', 'dark'] as const).map((t) => (
                  <button
                    key={t} type="button"
                    onClick={() => { setTheme(t); applyAppTheme(t); }}
                    className={cn(
                      'flex-1 py-2 rounded-md border text-xs font-medium capitalize',
                      theme === t
                        ? 'border-sky-500/60 bg-sky-500/10 text-sky-700 dark:text-sky-300'
                        : 'border-border text-muted-foreground',
                    )}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                Overrides household default. "System" follows your device.
              </p>
            </div>

            <div className="flex items-center gap-2 pt-2 border-t border-border">
              <Button
                type="submit"
                className="bg-sky-600 hover:bg-sky-700 text-white"
                disabled={updateMut.isPending}
              >
                <Save className="w-3.5 h-3.5 mr-1" />
                {updateMut.isPending ? 'Saving…' : 'Save profile'}
              </Button>
              {saved && (
                <span className="text-xs text-emerald-600 flex items-center gap-1">
                  <Check className="w-3 h-3" /> Saved
                </span>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
